import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FileText,
  Printer,
  Download,
  Eye,
  Search,
  Building2,
  Calendar,
  CheckCircle2,
  Receipt,
  QrCode,
} from "lucide-react";

export default function Invoices() {
  const { user } = useAuth();
  const isDealer = user?.role === "dealer";
  const [search, setSearch] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ["invoices-list"],
    queryFn: async () => {
      const { data } = await api.get("/invoices");
      return Array.isArray(data) ? data : [];
    },
  });

  const filtered = invoices.filter((inv) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (inv.doc_num || "").toLowerCase().includes(term) ||
      (inv.dealer_name || "").toLowerCase().includes(term) ||
      (inv.dealer_code || "").toLowerCase().includes(term)
    );
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">Invoicing & Billing</div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight text-slate-900" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
            Tax E-Invoices
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Official GST-compliant tax invoices extracted directly from SAP Business One A/R Invoice ledgers.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search invoice or dealer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-60"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <Receipt size={32} className="mx-auto text-slate-400 mb-2" />
          <div className="text-sm font-medium">No tax invoices found.</div>
          <div className="text-xs text-slate-400 mt-1">Invoices synced from SAP B1 will appear here.</div>
        </Card>
      ) : (
        <Card className="overflow-hidden border-slate-200 shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[800px]">
              <thead className="bg-slate-100/70 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                <tr>
                  <th className="py-3 px-4 text-left">Invoice No</th>
                  <th className="py-3 px-4 text-left">Date</th>
                  <th className="py-3 px-4 text-left">Customer / Dealer</th>
                  <th className="py-3 px-4 text-right">Taxable Value</th>
                  <th className="py-3 px-4 text-right">Tax Amount</th>
                  <th className="py-3 px-4 text-right">Invoice Total</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((inv) => (
                  <tr key={inv.id || inv.invoice_id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4 font-mono font-semibold text-blue-700">{inv.doc_num}</td>
                    <td className="py-3 px-4 font-mono text-slate-600">{new Date(inv.doc_date).toLocaleDateString("en-IN")}</td>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{inv.dealer_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{inv.dealer_code} • GSTIN: {inv.gstin || "N/A"}</div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-slate-700">{fmtINR(inv.total_before_tax || 0)}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-700">{fmtINR(inv.tax_amount || 0)}</td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-slate-900">{fmtINR(inv.total_amount || 0)}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                        {inv.status || "Open"}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedInvoice(inv)}
                        className="h-7 px-2.5 text-xs font-semibold"
                      >
                        <Eye size={12} className="mr-1" /> View E-Invoice
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Dynamic Tax E-Invoice Printable Modal */}
      {selectedInvoice && (
        <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-8 bg-white text-slate-900 font-sans">
            <DialogHeader className="flex flex-row items-center justify-between border-b pb-4">
              <DialogTitle className="text-xl font-bold">TAX INVOICE (GST)</DialogTitle>
              <Button size="sm" onClick={handlePrint} className="bg-slate-900 text-white text-xs">
                <Printer size={13} className="mr-1.5" /> Print / Save PDF
              </Button>
            </DialogHeader>

            <div className="space-y-6 pt-4 text-xs">
              {/* Header Box */}
              <div className="grid grid-cols-2 gap-6 border-b pb-4">
                <div>
                  <div className="font-bold text-sm text-slate-900">BHAWAR SALES CORPORATION</div>
                  <div className="text-slate-600 mt-1">Authorised Samsung Distributor</div>
                  <div className="text-slate-600">8/166, Upparapalayam Road, Edapalayam Village, Chennai - 600052</div>
                  <div className="text-slate-600 font-semibold mt-1">GSTIN: 33AAEFB2116Q1ZK • State Code: 33</div>
                </div>
                <div className="text-right">
                  <div className="text-slate-500 uppercase font-semibold text-[10px]">Invoice Number</div>
                  <div className="text-lg font-bold font-mono text-blue-700">{selectedInvoice.doc_num}</div>
                  <div className="text-slate-600 mt-1">Invoice Date: <span className="font-semibold">{new Date(selectedInvoice.doc_date).toLocaleDateString("en-IN")}</span></div>
                  <div className="text-slate-600">Place of Supply: <span className="font-semibold">{selectedInvoice.place_of_supply || "33 - Tamil Nadu"}</span></div>
                </div>
              </div>

              {/* Bill To & Ship To */}
              <div className="grid grid-cols-2 gap-6 bg-slate-50 p-4 rounded-lg border">
                <div>
                  <div className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">Billed To (Customer):</div>
                  <div className="font-bold text-slate-900 mt-1 text-sm">{selectedInvoice.dealer_name}</div>
                  <div className="text-slate-600">Dealer Code: <span className="font-mono font-semibold">{selectedInvoice.dealer_code}</span></div>
                  <div className="text-slate-600 font-semibold">GSTIN: {selectedInvoice.gstin || "URP"}</div>
                </div>
                <div>
                  <div className="font-bold text-slate-700 uppercase tracking-wider text-[10px]">Shipped To:</div>
                  <div className="font-semibold text-slate-900 mt-1">{selectedInvoice.dealer_name}</div>
                  <div className="text-slate-600">{selectedInvoice.place_of_supply || "Tamil Nadu, India"}</div>
                </div>
              </div>

              {/* Items Table */}
              <table className="w-full border-collapse border border-slate-200 text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-semibold">
                    <th className="border p-2 text-center">#</th>
                    <th className="border p-2 text-left">Item Description</th>
                    <th className="border p-2 text-center">HSN</th>
                    <th className="border p-2 text-center">Qty</th>
                    <th className="border p-2 text-right">Rate (₹)</th>
                    <th className="border p-2 text-right">Taxable (₹)</th>
                    <th className="border p-2 text-right">GST (₹)</th>
                    <th className="border p-2 text-right">Total (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.lines || []).length > 0 ? (
                    selectedInvoice.lines.map((l, i) => (
                      <tr key={i}>
                        <td className="border p-2 text-center">{i + 1}</td>
                        <td className="border p-2 font-medium">{l.ItemDescription || l.name}</td>
                        <td className="border p-2 text-center font-mono">{l.HSNCode || "8517"}</td>
                        <td className="border p-2 text-center">{l.Quantity || 1}</td>
                        <td className="border p-2 text-right font-mono">{fmtINR(l.Price || 0)}</td>
                        <td className="border p-2 text-right font-mono">{fmtINR(l.LineTotal || 0)}</td>
                        <td className="border p-2 text-right font-mono">{fmtINR(l.TaxTotal || 0)}</td>
                        <td className="border p-2 text-right font-mono font-bold">{fmtINR((l.LineTotal || 0) + (l.TaxTotal || 0))}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="border p-2 text-center">1</td>
                      <td className="border p-2 font-medium">Samsung Mobile & IT Equipment Supply</td>
                      <td className="border p-2 text-center font-mono">8517</td>
                      <td className="border p-2 text-center">1</td>
                      <td className="border p-2 text-right font-mono">{fmtINR(selectedInvoice.total_before_tax)}</td>
                      <td className="border p-2 text-right font-mono">{fmtINR(selectedInvoice.total_before_tax)}</td>
                      <td className="border p-2 text-right font-mono">{fmtINR(selectedInvoice.tax_amount)}</td>
                      <td className="border p-2 text-right font-mono font-bold">{fmtINR(selectedInvoice.total_amount)}</td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold">
                    <td colSpan={7} className="border p-2 text-right">Grand Total (INR):</td>
                    <td className="border p-2 text-right font-mono text-sm">{fmtINR(selectedInvoice.total_amount)}</td>
                  </tr>
                </tfoot>
              </table>

              {/* Bank Transfer Details & Statutory Sign-off */}
              <div className="grid grid-cols-2 gap-6 border-t pt-4 text-[11px] text-slate-600">
                <div>
                  <div className="font-bold text-slate-800 uppercase text-[10px]">Bank Remittance Details:</div>
                  <div>Account Name: <span className="font-semibold">BHAWAR SALES CORPORATION</span></div>
                  <div>Bank: <span className="font-semibold">HDFC Bank Ltd</span></div>
                  <div>Account No: <span className="font-mono font-semibold">50200028912891</span></div>
                  <div>IFSC Code: <span className="font-mono font-semibold">HDFC0000004</span></div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-800">For BHAWAR SALES CORPORATION</div>
                  <div className="mt-8 border-t border-slate-300 pt-1 text-[10px] text-slate-400">Authorized Signatory</div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
